import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import {
  BasicEquipment,
  BasicEquipmentDefault,
  EquipmentTypeahead,
} from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const equipmentSelectorSchema = yup.object().shape({
  equipment: yup
    .object()
    .shape({
      name: yup.string().notRequired(),
      id: yup.string().notRequired(),
      external_id: yup.string().notRequired(),
      equipment_type: yup.object().shape({
        name: yup.string().notRequired(),
        id: yup.string().notRequired(),
      }),
    })
    .nullable(),
});

export const getEquipmentLabel = (equipment?: EquipmentTypeahead) => {
  if (equipment?.companyShare) {
    return equipment?.companyShare.externalId
      ? `${equipment.companyShare.externalId} (${equipment.name}) • ${equipment?.equipmentType?.name}`
      : `${equipment.name} • ${equipment?.equipmentType?.name}`;
  } else if (equipment) {
    return equipment.equipmentType.name
      ? `${equipment.name} • ${equipment?.equipmentType?.name}`
      : equipment.name;
  } else {
    return '';
  }
};

type EquipmentSelectorDTO = yup.InferType<typeof equipmentSelectorSchema>;

interface EquipmentSelectorProps {
  defaultEquipment: Nullable<BasicEquipment | BasicEquipmentDefault>;
  disabled: boolean;
  onSelect: (data: Nullable<EquipmentTypeahead>) => Promise<boolean> | void;
  clearable?: boolean;
  companyId?: string;
  managedCompanyId?: string;
  placeholder?: string;
  shared?: boolean;
}

export const EquipmentSelector = observer(
  ({
    defaultEquipment,
    disabled,
    onSelect,
    clearable = true,
    companyId,
    managedCompanyId,
    placeholder,
    shared,
  }: EquipmentSelectorProps) => {
    const { getEquipmentByCompanyIdTypeahead } = useEquipment();
    const { userStore } = useStores();
    const effectiveCompanyId = companyId || getEffectiveUserCompanyId(userStore);
    const getEquipmentByCompanyIdTypeaheadSafe = useCallback(
      async (options: Parameters<typeof getEquipmentByCompanyIdTypeahead>[0]) => {
        try {
          return await getEquipmentByCompanyIdTypeahead(options);
        } catch {
          // Error is already handled in the hook
          return { data: [], pagination: undefined };
        }
      },
      [getEquipmentByCompanyIdTypeahead],
    );
    useState<Nullable<EquipmentTypeahead>>(null);

    const {
      control,
      getValues,
      reset,
      formState: { errors },
    } = useForm<EquipmentSelectorDTO>({
      resolver: yupResolver(equipmentSelectorSchema),
      reValidateMode: 'onChange',
      defaultValues: {
        equipment: defaultEquipment,
      },
    });

    useEffect(() => {
      reset({
        equipment: defaultEquipment,
      });
    }, [defaultEquipment, reset]);

    const handleSelect = async (equipment: Nullable<EquipmentTypeahead>) => {
      reset({ equipment });
      const isSaved = await onSelect(equipment);

      if (isSaved === false) {
        reset({ equipment: defaultEquipment });
      }

      return getValues('equipment');
    };

    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <AutocompleteAsyncFormField
          control={control}
          errors={errors}
          name={'equipment'}
          getValue={(item) => item?.id || ''}
          getLabel={(item: EquipmentTypeahead) => (item ? getEquipmentLabel(item) : '')}
          asyncCallback={getEquipmentByCompanyIdTypeaheadSafe}
          extraRequestOptions={{
            companyId: effectiveCompanyId,
            managedCompanyId,
            dispatchable: true,
            shared,
          }}
          filterSelectedOptions={false}
          clearable={clearable}
          hideLabel
          disablePortal={false}
          sx={{ mt: 0.5 }}
          inputProps={{
            onKeyDown: (event) => {
              if (event.key === ' ') {
                // override MUI's default behavior of consuming space key to prevent scrolling.
                // https://github.com/mui/material-ui/issues/34566
                event.stopPropagation();
              }
            },
            sx: {
              fontSize: '12px',
            },
          }}
          onSelect={handleSelect}
          onClear={() => void handleSelect(null)}
          disabled={disabled}
          placeholder={placeholder ?? `${t('form_fields.select_truck')}`}
          defaultValue={defaultEquipment}
        />
      </Box>
    );
  },
);
